import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { IsoToLocalDateTimeMapper } from '@shared-classes';
import { NoteStatus } from '@shared-enums';
import { NotePdf } from '@shared-models';
import {
  DianService,
  PdfElectronicContentService,
  PdfElectronicInfoService,
} from '@shared-services';
import { FormatInvoiceNumberService } from '@shared-services';

import { InvoiceStatusDescription } from 'src/app/@enums/pdf/invoice-status.enum';
import { INotasGrap } from 'src/app/@interfaces/notas.interface';
import { GenerateDianContentPdfService } from 'src/app/@pages/details/generate-dian-content-pdf.service';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';
import { GenerateContentPdfService } from 'src/app/@shared/components/services/files/generate-content-pdf.service';
import { PdfConfiguration } from 'src/app/@shared/interfaces/pdf.interface';

@Injectable({
  providedIn: 'root',
})
export class ElectronicNotePdfService {
  private generateContentPdfService = inject(GenerateContentPdfService);
  private pdfGeneratorService = inject(PdfGeneratorService);
  private generateDianContentPdfService = inject(GenerateDianContentPdfService);
  private translate = inject(TranslateService);
  private pdfElectronicInfoService = inject(PdfElectronicInfoService);
  private pdfElectronicContentService = inject(PdfElectronicContentService);
  private dianService = inject(DianService);
  private formatInvoiceNumber = inject(FormatInvoiceNumberService);

  async createNotePdf(object: NotePdf) {
    const activeCompanyDetails = await firstValueFrom(
      this.pdfElectronicInfoService.getActiveCompanyDetail(),
    );

    const noteStatus = this.getPdfNoteStatusInfo(object.noteObject);
    const electronicNote = this.getElectronicNoteCode(object);
    const parsedDate = this.getParsedNoteDate(object);

    const emitter = this.pdfElectronicInfoService.buildEmitter(
      activeCompanyDetails!,
    );
    const prefix = this.pdfElectronicInfoService.getPrefix(
      activeCompanyDetails!,
      object.noteCount?.voucher ?? 0,
    );
    const dian = this.pdfElectronicInfoService.buildDianData(
      object.noteCount?.cude || object.cude || '',
      prefix,
    );
    const client = this.pdfElectronicInfoService.buildClient(
      object.noteObject.invoice?.cliente,
    );
    const emission = this.pdfElectronicInfoService.buildEmission(
      object.noteObject.dueDate,
      electronicNote,
      parsedDate,
    );

    const items = this.pdfElectronicInfoService.generatePdfItems(
      object.noteValues,
      object.noteObject.invoice!,
    );
    const totals = this.pdfElectronicInfoService.buildTotals(object);

    const legal = this.buildNoteLegalText(dian.cufe);
    const logoObject =
      await this.generateContentPdfService.generatePdfImageLogo(object.company);

    const emitterInfo =
      await this.generateDianContentPdfService.createEmitterInfo(
        emitter,
        logoObject,
      );

    const concept = await this.getNoteConcept(
      object.noteObject.concepto,
      object.noteObject.tipo_nota,
    );
    const noteObservations = `${object.noteObject.invoice?.observations || 'N/A'} \n ${this.translate.instant('Concept')}: ${concept}`;

    const content = this.pdfElectronicContentService.buildContent(
      noteObservations,
      emitterInfo,
      dian,
      client,
      emission,
      items,
      totals,
      noteStatus.type,
    );
    const styles = this.pdfElectronicInfoService.buildStyles();

    if (dian.cufe) {
      object.qrUrl = this.pdfElectronicInfoService.generateQr(
        object,
        electronicNote,
        parsedDate,
        emitter,
        client,
        totals,
        dian,
      );
    }

    const pdfConfiguration: PdfConfiguration = {
      openPDF: object.open!,
      downloadPDF: false,
      content,
      pageMargins: [40, 160, 40, 80],
      pageSize: 'A4',
      styles,
      header: () => [
        this.generateDianContentPdfService.createHeaderInvoice(
          noteStatus.id,
          noteStatus.type,
          electronicNote,
          object.qrUrl,
        ),
      ],
      footer: () => this.generateDianContentPdfService.createLegalFooter(legal),
    };

    return this.pdfGeneratorService.createPdf(pdfConfiguration);
  }

  private getElectronicNoteCode(object: NotePdf): string {
    return (
      this.formatInvoiceNumber.formatElectronicNumber(
        object.noteCount?.note_number || '',
        object.noteCount?.voucher || 0,
      ) || this.translate.instant('Pending')
    );
  }

  private getParsedNoteDate(object: NotePdf) {
    return IsoToLocalDateTimeMapper.mapper(object.noteObject.createAt);
  }

  private buildNoteLegalText(cude: string) {
    return (
      'Esta nota corresponde a un Documento Electrónico tipo CreditNote o DebitNote (formato UBL XML), emitida por medio de V1-INSIGHT Software autorizado por la DIAN. Puede validar la autenticidad de esta factura en la página oficial de la DIAN: www.dian.gov.co utilizando el código CUDE: ' +
      cude
    );
  }

  private getPdfNoteStatusInfo(noteObject: INotasGrap) {
    let status: { type: string; id: string } = {
      type: '',
      id: '',
    };
    switch (noteObject.tipo_nota) {
      case NoteStatus.CREDITNOTE:
        status = {
          type: `${this.translate.instant('Credit Note')}`,
          id: `CN-${noteObject.id_nota}`,
        };
        break;
      case NoteStatus.DEBITNOTE:
        status = {
          type: `${this.translate.instant('Debit Note')}`,
          id: `DN-${noteObject.id_nota}`,
        };
        break;
      default:
        status = { type: InvoiceStatusDescription.Unknown, id: '' };
        break;
    }
    return status;
  }

  private getNoteConcept(concept: string, noteType: string) {
    return new Promise<string>((resolve, reject) => {
      // 91 is the code used to identify a credit note, 92 is debit note, so it is used here to bring in the credit or debit note concepts from the DIAN
      const type = noteType === 'Credito' ? 91 : 92;
      this.dianService.getDianNoteConcepts(type).subscribe({
        next: (response) => {
          const foundConcept = response.data.find(
            (item) => item.code === concept,
          );
          if (foundConcept) {
            resolve(foundConcept.description);
          } else {
            reject(`Concept "${concept}" not found.`);
          }
        },
        error: (error) => {
          reject(`Error fetching concepts: ${error}`);
        },
      });
    });
  }
}
